import { useState } from 'react';
import { isAxiosError } from 'axios';
import { authApi } from '@/api/auth.api';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useLoginWelcome } from '@/hooks/use-login-welcome';
import {
  ApiErrorResponse,
  AuthSession,
  LoginPayload,
  RegisterPayload,
} from '@/types/auth.types';

export const useAuthActions = () => {
  const { setUser, t } = useApp();
  const { toast } = useToast();
  const greet = useLoginWelcome();
  const [loading, setLoading] = useState(false);

  const getApiErrorText = (error: unknown): string => {
    if (!isAxiosError(error)) {
      if (error instanceof Error) {
        return error.message;
      }
      return '';
    }

    const payload = error.response?.data;
    if (typeof payload === 'string') {
      return payload;
    }

    if (payload && typeof payload === 'object') {
      const record = payload as Record<string, unknown>;
      const candidates = [record.message, record.error, record.detail];
      const firstText = candidates.find((value) => typeof value === 'string' && value.trim());
      if (typeof firstText === 'string') {
        return firstText;
      }
    }

    return error.message ?? '';
  };

  const getRegisterErrorMessage = (error: unknown, username?: string): string => {
    if (isAxiosError<ApiErrorResponse>(error)) {
      const apiMessage = getApiErrorText(error).toLowerCase();
      const isDuplicateUsernameError =
        error.response?.status === 409 ||
        apiMessage.includes('duplicate key value violates unique constraint') ||
        apiMessage.includes('violates unique constraint') ||
        apiMessage.includes('duplicate key') ||
        (apiMessage.includes('username') && (apiMessage.includes('exist') || apiMessage.includes('taken') || apiMessage.includes('duplicate'))) ||
        apiMessage.includes('nom d\'utilisateur existe') ||
        apiMessage.includes('nom d\'utilisateur déjà utilisé') ||
        apiMessage.includes('username already exists') ||
        apiMessage.includes('username already taken');

      if (isDuplicateUsernameError) {
        return username
          ? t.usernameExists.replace("{username}", username)
          : t.usernameExistsGeneric;
      }
    }

    return extractErrorMessage(error);
  };

  const extractErrorMessage = (error: unknown): string => {
    if (isAxiosError<ApiErrorResponse>(error)) {
      const apiText = getApiErrorText(error);
      return apiText || error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return t.unexpectedError;
  };

  const persistAuthFeedback = (session: AuthSession, message: string) => {
    setUser(session.user);
    toast({ title: message, variant: 'default' });
  };

  const register = async (data: RegisterPayload) => {
    setLoading(true);
    try {
      const session = await authApi.register(data);
      persistAuthFeedback(session, t.registerSuccess);
      return session;
    } catch (error: unknown) {
      const message = getRegisterErrorMessage(error, data.username);
      toast({ title: t.authError, description: message, variant: 'destructive' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getLoginErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
      if (!error.response) {
        return t.loginNetworkError;
      }
      const apiText = getApiErrorText(error).toLowerCase();
      // Unknown account, wrong password and ambiguous identifier all come back
      // as the same generic 401 on purpose. Other 401s (inactive account) keep
      // their own message.
      if (error.response.status === 401 && (!apiText || apiText.includes('invalid credentials'))) {
        return t.invalidCredentials;
      }
    }
    return extractErrorMessage(error);
  };

  const login = async (data: LoginPayload) => {
    setLoading(true);
    try {
      const session = await authApi.login(data);
      setUser(session.user);
      greet(session.user, session.isFirstLogin);
      return session;
    } catch (error: unknown) {
      const normalizedMessage = extractErrorMessage(error).toLowerCase();
      const isAccountStatusError =
        normalizedMessage.includes('suspended') || normalizedMessage.includes('banned');

      if (!isAccountStatusError) {
        toast({ title: t.loginFailedTitle, description: getLoginErrorMessage(error), variant: 'destructive' });
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { register, login, loading };
};
